import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { STATUS_LABELS } from "@/lib/i18n";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ExternalLink, Film, Mail, Pencil, Check, X } from "lucide-react";

const STATUSES = ["EN ATTENTE DE PAIEMENT", "PAYEE", "EN COURS DE PRODUCTION", "LIVREE"];
const ACTIVE = ["EN ATTENTE DE PAIEMENT", "PAYEE", "EN COURS DE PRODUCTION"];

export default function AdminOrders() {
  const { t, lang, money } = useLang();
  const [orders, setOrders] = useState([]);
  const [active, setActive] = useState(null);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [editIdx, setEditIdx] = useState(null);
  const [editTitle, setEditTitle] = useState("");

  const load = useCallback(() => {
    api.get("/orders", { params: { scope: "active" } }).then((r) => setOrders(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openOrder = (o) => { setActive(o); setUrl(""); setTitle(""); setEditIdx(null); setEditTitle(""); };
  const refreshActive = (o) => {
    setActive(o);
    setOrders((prev) => prev.map((x) => (x.id === o.id ? o : x)).filter((x) => ACTIVE.includes(x.status)));
  };

  const addVideo = async () => {
    if (!url.trim()) return toast.error(t("ao.need_link"));
    try {
      const { data } = await api.post(`/orders/${active.id}/videos`, { url: url.trim(), title: title.trim() });
      toast.success(t("ao.video_added"));
      setUrl(""); setTitle("");
      refreshActive(data);
      if (data.status === "LIVREE") { toast.success(t("ao.pack_done")); setActive(null); load(); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const removeVideo = async (i) => {
    const { data } = await api.delete(`/orders/${active.id}/videos/${i}`);
    refreshActive(data);
  };

  const startEdit = (i, current) => { setEditIdx(i); setEditTitle(current || ""); };
  const saveTitle = async (i) => {
    try {
      const { data } = await api.put(`/orders/${active.id}/videos/${i}`, { title: editTitle.trim() });
      toast.success(t("ao.title_updated"));
      setEditIdx(null); setEditTitle("");
      refreshActive(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resendEmail = async () => {
    try {
      await api.post(`/orders/${active.id}/resend-delivery-email`);
      toast.success(t("ao.resend_sent"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (o, status) => {
    try {
      const { data } = await api.patch(`/orders/${o.id}/status`, { status });
      toast.success(t("ao.status_updated"));
      if (status === "LIVREE") { setOrders((p) => p.filter((x) => x.id !== o.id)); setActive(null); }
      else { setActive(data); setOrders((p) => p.map((x) => (x.id === o.id ? data : x))); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="ch-fade-up space-y-8">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("ao.eyebrow")}</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">{t("nav.active_orders")}</h1>
        <p className="mt-2 text-sm text-neutral-500">{t("ao.subtitle")}</p>
      </header>

      <div className="overflow-hidden rounded-sm border border-neutral-200">
        <div className="hidden grid-cols-12 gap-4 border-b border-neutral-200 bg-neutral-50 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-400 md:grid">
          <div className="col-span-3">{t("ao.col_client")}</div>
          <div className="col-span-3">{t("ao.col_pack")}</div>
          <div className="col-span-2">{t("ao.remaining")}</div>
          <div className="col-span-2">{t("common.status")}</div>
          <div className="col-span-2 text-right">{t("common.price")}</div>
        </div>
        {orders.length === 0 && <div className="px-6 py-12 text-center text-sm text-neutral-400">{t("ao.empty")}</div>}
        {orders.map((o) => (
          <button key={o.id} data-testid={`order-row-${o.id}`} onClick={() => openOrder(o)}
            className="grid w-full grid-cols-1 gap-2 border-b border-neutral-100 px-6 py-4 text-left transition-colors last:border-0 hover:bg-neutral-50 md:grid-cols-12 md:items-center md:gap-4">
            <div className="col-span-3 font-semibold">{o.client_name}</div>
            <div className="col-span-3 text-sm text-neutral-600">{o.package_name}</div>
            <div className="col-span-2">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold">
                <Film className="h-3.5 w-3.5" /> {o.videos_remaining}/{o.video_count}
              </span>
            </div>
            <div className="col-span-2"><StatusBadge status={o.status} /></div>
            <div className="col-span-2 font-display font-bold md:text-right">{money(o.price)}</div>
          </button>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent data-testid="order-detail-dialog" className="max-w-lg rounded-sm">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{active.client_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                <div className="flex items-center justify-between rounded-sm bg-black p-4 text-white">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-white/50">{active.package_name}</p>
                    <p className="mt-1 text-sm text-white/70">{active.seconds_per_video}{t("ao.per_video")} · {money(active.price)}</p>
                  </div>
                  <div className="text-right">
                    <p data-testid="order-remaining-count" className="font-display text-3xl font-extrabold">{active.videos_remaining}</p>
                    <p className="text-xs text-white/50">{t("ao.remaining_label")}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t("common.status")}</Label>
                  <Select value={active.status} onValueChange={(v) => changeStatus(active, v)}>
                    <SelectTrigger data-testid="order-status-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[lang]?.[s] || s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t("ao.video_links")}</Label>
                  {active.videos.length === 0 && <p className="text-sm text-neutral-400">{t("ao.no_videos")}</p>}
                  <ul className="space-y-2">
                    {active.videos.map((v, i) => (
                      <li key={i} className="flex items-center justify-between rounded-sm border border-neutral-200 px-3 py-2 text-sm">
                        {editIdx === i ? (
                          <div className="flex w-full items-center gap-2">
                            <Input data-testid={`edit-video-title-input-${i}`} value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                              placeholder={t("ao.title_ph")} className="h-8 rounded-sm" autoFocus
                              onKeyDown={(e) => e.key === "Enter" && saveTitle(i)} />
                            <button data-testid={`save-video-title-${i}`} onClick={() => saveTitle(i)} className="shrink-0 text-neutral-500 hover:text-black"><Check className="h-4 w-4" /></button>
                            <button data-testid={`cancel-video-title-${i}`} onClick={() => setEditIdx(null)} className="shrink-0 text-neutral-400 hover:text-black"><X className="h-4 w-4" /></button>
                          </div>
                        ) : (
                          <>
                            <a href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 truncate hover:underline">
                              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate">{v.title || v.url}</span>
                            </a>
                            <div className="ml-2 flex shrink-0 items-center gap-2">
                              <button data-testid={`edit-video-${i}`} onClick={() => startEdit(i, v.title)} className="text-neutral-400 hover:text-black" title={t("ao.edit_title")}>
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button data-testid={`remove-video-${i}`} onClick={() => removeVideo(i)} className="text-neutral-400 hover:text-red-500">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                  {active.videos.length > 0 && (
                    <Button variant="outline" data-testid="resend-email-button" onClick={resendEmail}
                      className="mt-2 w-full gap-2 rounded-sm border-black">
                      <Mail className="h-4 w-4" /> {t("ao.resend_email")}
                    </Button>
                  )}
                </div>

                {active.videos_remaining > 0 && (
                  <div className="space-y-2 rounded-sm bg-neutral-50 p-4">
                    <Input data-testid="video-title-input" placeholder={t("ao.title_ph")} value={title} onChange={(e) => setTitle(e.target.value)} className="h-10 rounded-sm bg-white" />
                    <div className="flex gap-2">
                      <Input data-testid="video-url-input" placeholder="https://..." value={url} onChange={(e) => setUrl(e.target.value)} className="h-10 rounded-sm bg-white" />
                      <Button data-testid="add-video-button" onClick={addVideo} className="h-10 shrink-0 rounded-sm bg-black hover:bg-neutral-800">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
